import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import {
    ReservationListMedicalFoodPreferenceType,
    type ReservationListProfileFieldsDTO,
    type WeeklyPlannerReservationListRequestDTO,
    type WeeklyPlannerReservationListRowDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {
    type Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ReservationAggregationService} from "crqlar-react-common/dist/services/aggregation/reservationAggregation.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {addMinutes, endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useReservationFilterOptions} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import RangeDatePicker from "../../common/newDatePicker/RangeDatePicker";
import {useDebouncedDateRange} from "../../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
} from "../../common/serverTable";
import {AllergiesElement} from "../../common/uiElements/AllergiesElement";
import {AgeCategoriesElement} from "../../common/uiElements/AgeCategoriesElement";
import {AliasAgeCategoriesElement} from "../../common/uiElements/AliasAgeCategoriesElement";
import {BirthdayElement} from "../../common/uiElements/BirthdayElement";
import {CountryElement} from "../../common/uiElements/CountryElement";
import {DateElement} from "../../common/uiElements/DateElement";
import {DietsElement} from "../../common/uiElements/DietsElement";
import {GenderElement} from "../../common/uiElements/GenderElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {MedicalElement} from "../../common/uiElements/MedicalElement";
import {NationalityElement} from "../../common/uiElements/NationalityElement";
import {NotesElement} from "../../common/uiElements/NotesElement";
import {noteTexts} from "crqlar-react-common/dist/utils/noteTypeGroup.util";
import {RecommendationsElement} from "../../common/uiElements/RecommendationsElement";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";
import {SimpleChipElement} from "../../common/uiElements/SimpleChipElement";
import {getLocalizedValue} from "../../common/uiElements/utils";
import {ReservationServicePicker} from "../rail/ReservationServicePicker";
import {useReservationServiceSelection} from "../rail/useReservationServiceSelection";
import ReservationDialog from "../reservationDialog/ReservationDialog";
import {createReservationUpdatedColumn} from "./reservationUpdatedColumn";

const displayName = (row: ReservationListProfileFieldsDTO) => (
    row.fullName ?? [row.firstName, row.lastName].filter(Boolean).join(" ")
);

const localizedInterestValues = (row: ReservationListProfileFieldsDTO, locale: string) => (
    (row.interests ?? [])
        .map(interest => getLocalizedValue(interest.localizations, locale) ?? interest.value)
        .filter((value): value is string => Boolean(value))
);

const preferenceValues = (
    row: ReservationListProfileFieldsDTO,
    preferenceType: ReservationListMedicalFoodPreferenceType,
) => (
    (row.medicalFoodPreferences ?? [])
        .filter(preference => preference.preferenceType === preferenceType)
        .map(preference => preference.preferenceValue)
        .filter(Boolean)
);

export default function WeeklyPlannerReservationList({sectorId}: { sectorId: string }) {
    const {t, i18n} = useTranslation();
    const readOnly = useReadOnly();
    const filterOptionsQuery = useReservationFilterOptions();
    const ageCategories = useAllAgeCategories();
    const serviceSelection = useReservationServiceSelection({sectorId, viewKey: "list"});
    const locale = i18n.resolvedLanguage || i18n.language;
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const {range: dateRange, queryRange, onRangeChange} = useDebouncedDateRange(sectorId);
    const selectedServiceIds = serviceSelection.services.length === 0 || serviceSelection.allSelected
        ? undefined
        : serviceSelection.selectedIds;
    const selectedServiceIdsKey = selectedServiceIds?.join(",") ?? "all";
    const noServicesSelected = serviceSelection.services.length > 0
        && serviceSelection.selectedIds.length === 0;

    const handleOpenReservation = useCallback(async (row: WeeklyPlannerReservationListRowDTO) => {
        try {
            const reservation = await ReservationService.getReservationById(row.id);
            if (!reservation) throw new Error("Reservation not found");
            setSelectedReservation(reservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {variant: "error"});
        }
    }, [t]);

    const handleCreateReservation = useCallback(() => {
        const now = new Date();
        const start = new Date(dateRange.start);
        start.setHours(now.getHours(), now.getMinutes(), 0, 0);

        setSelectedReservation({
            expectedStartDate: start,
            expectedEndDate: addMinutes(start, 60),
            sectorId,
            status: ReservationStatus.CONFIRMED,
        });
    }, [dateRange.start, sectorId]);

    const columns = useMemo<ColumnDef<WeeklyPlannerReservationListRowDTO>[]>(() => [
        {
            id: "guest",
            header: t("Primary guest"),
            accessorFn: displayName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "serviceName",
            header: t("Service"),
            accessorFn: row => row.serviceName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 280,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                grow: true,
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "status",
            header: t("Status"),
            accessorFn: row => row.status,
            cell: ({row}) => (
                <ReservationStatusElement
                    status={row.original.status}
                    start={row.original.start}
                    end={row.original.end}
                />
            ),
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <ReservationStatusElement status={row.original.status} asPrint/>,
            },
        },
        {
            id: "start",
            header: t("Start"),
            accessorFn: row => row.start,
            cell: ({row}) => <DateElement date={row.original.start}/>,
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.start}/>,
            },
        },
        {
            id: "end",
            header: t("End"),
            accessorFn: row => row.end,
            cell: ({row}) => <DateElement date={row.original.end}/>,
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.end}/>,
            },
        },
        {
            id: "resources",
            header: t("Resources"),
            accessorFn: row => row.resourceNames ?? [],
            cell: ({row}) => <SimpleChipElement values={row.original.resourceNames}/>,
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "resourceNames",
                printable: true,
                printDefault: true,
                printCell: row => <SimpleChipElement values={row.original.resourceNames} asPrint/>,
            },
        },
        {
            id: "guestCount",
            header: t("Guest count abbreviation"),
            accessorFn: row => row.guestCount,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 120,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "guests",
            header: t("Age categories"),
            accessorFn: row => (
                (row.computedAgeCategories?.adults ?? 0)
                + (row.computedAgeCategories?.children ?? 0)
            ),
            cell: ({row}) => (
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    birthdays={row.original.birthday ? [row.original.birthday] : []}
                    referenceDate={row.original.start ?? queryRange.start}
                    computedAgeCategories={row.original.computedAgeCategories}
                />
            ),
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "computedAgeCategories",
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AgeCategoriesElement
                        ageCategories={ageCategories}
                        birthdays={row.original.birthday ? [row.original.birthday] : []}
                        referenceDate={row.original.start ?? queryRange.start}
                        computedAgeCategories={row.original.computedAgeCategories}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "aliasAgeCategories",
            header: `${t("Age categories")} (${t("Alias")})`,
            accessorFn: row => row.aliasAgeCategories,
            cell: ({row}) => (
                <AliasAgeCategoriesElement
                    aliasAgeCategories={row.original.aliasAgeCategories}
                    ageCategories={ageCategories}
                />
            ),
            size: 160,
            meta: {
                defaultActive: false,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <AliasAgeCategoriesElement
                        aliasAgeCategories={row.original.aliasAgeCategories}
                        ageCategories={ageCategories}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "gender",
            header: t("Gender"),
            accessorFn: row => row.gender,
            cell: ({row}) => <GenderElement gender={row.original.gender}/>,
            size: 140,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <GenderElement gender={row.original.gender}/>,
            },
        },
        {
            id: "birthday",
            header: t("Birthday"),
            accessorFn: row => row.birthday,
            cell: ({row}) => (
                <BirthdayElement
                    birthday={row.original.birthday}
                    selectedDate={queryRange.start}
                    name={displayName(row.original)}
                />
            ),
            size: 170,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <BirthdayElement
                        birthday={row.original.birthday}
                        selectedDate={queryRange.start}
                        name={displayName(row.original)}
                    />
                ),
            },
        },
        {
            id: "languageCodes",
            header: t("Language"),
            accessorFn: row => row.languageCodes ?? [],
            cell: ({row}) => <LanguagesElement languageCodes={row.original.languageCodes}/>,
            size: 120,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <LanguagesElement languageCodes={row.original.languageCodes} asPrint/>,
            },
        },
        {
            id: "firstName",
            header: t("First name"),
            accessorFn: row => row.firstName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 180,
            meta: {defaultActive: false, isServerSortable: true, printable: true},
        },
        {
            id: "lastName",
            header: t("Last name"),
            accessorFn: row => row.lastName,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 180,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
            },
        },
        {
            id: "namePrefix",
            header: t("Name prefix"),
            accessorFn: row => row.namePrefix,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 150,
            meta: {defaultActive: false, printable: true},
        },
        {
            id: "resourceCategories",
            header: t("Resource categories"),
            accessorFn: row => row.resourceCategoryNames ?? [],
            cell: ({row}) => <SimpleChipElement values={row.original.resourceCategoryNames}/>,
            size: 260,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                serverSortField: "resourceCategoryNames",
                printable: true,
                printCell: row => <SimpleChipElement values={row.original.resourceCategoryNames} asPrint/>,
            },
        },
        {
            id: "countryCode",
            header: t("Country"),
            accessorFn: row => row.countryCode,
            cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
            size: 130,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>,
            },
        },
        {
            id: "nationality",
            header: t("Nationality"),
            accessorFn: row => row.nationality,
            cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
            size: 130,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>,
            },
        },
        {
            id: "interests",
            header: t("Interests"),
            accessorFn: row => localizedInterestValues(row, locale),
            cell: ({row}) => (
                <SimpleChipElement values={localizedInterestValues(row.original, locale)}/>
            ),
            size: 260,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <SimpleChipElement values={localizedInterestValues(row.original, locale)} asPrint/>
                ),
            },
        },
        {
            id: "allergiesAndDiets",
            header: t("Allergies & diets"),
            accessorFn: row => [
                ...preferenceValues(row, ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY),
                ...preferenceValues(row, ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE),
            ],
            cell: ({row}) => {
                const allergies = preferenceValues(
                    row.original,
                    ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY,
                );
                const diets = preferenceValues(
                    row.original,
                    ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE,
                );
                if (!allergies.length && !diets.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        <AllergiesElement allergies={allergies}/>
                        <DietsElement diets={diets}/>
                    </Box>
                );
            },
            size: 300,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <SimpleChipElement
                        values={[
                            ...preferenceValues(
                                row.original,
                                ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_ALLERGY,
                            ),
                            ...preferenceValues(
                                row.original,
                                ReservationListMedicalFoodPreferenceType.CRQLAR_FOOD_TYPE,
                            ),
                        ]}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "medical",
            header: t("Medical"),
            accessorFn: row => preferenceValues(
                row,
                ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
            ),
            cell: ({row}) => (
                <MedicalElement
                    medical={preferenceValues(
                        row.original,
                        ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
                    )}
                />
            ),
            size: 240,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <SimpleChipElement
                        values={preferenceValues(
                            row.original,
                            ReservationListMedicalFoodPreferenceType.CRQLAR_MEDICAL,
                        )}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "recommendations",
            header: t("Recommendations"),
            accessorFn: row => [
                ...(row.warningRecommendation ?? []),
                ...(row.informationRecommendation ?? []),
                ...(row.successRecommendation ?? []),
            ],
            cell: ({row}) => (
                <RecommendationsElement
                    warningMessages={row.original.warningRecommendation}
                    informationMessages={row.original.informationRecommendation}
                    successMessages={row.original.successRecommendation}
                    locale={locale}
                />
            ),
            size: 300,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => (
                    <RecommendationsElement
                        warningMessages={row.original.warningRecommendation}
                        informationMessages={row.original.informationRecommendation}
                        successMessages={row.original.successRecommendation}
                        locale={locale}
                        printMode
                    />
                ),
            },
        },
        {
            id: "notes",
            header: t("Notes"),
            accessorFn: row => noteTexts(row.notes),
            cell: ({row}) => <NotesElement notes={row.original.notes}/>,
            size: 280,
            meta: {
                defaultActive: false,
                printable: true,
                printCell: row => <NotesElement notes={row.original.notes} asPrint/>,
            },
        },
        {
            id: "source",
            header: t("Source"),
            accessorFn: row => row.fromBookingEngine,
            cell: ({row}) => t(row.original.fromBookingEngine ? "Online Reservation" : "Staff"),
            size: 170,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                serverSortField: "fromBookingEngine",
                printable: true,
                printCell: row => t(row.original.fromBookingEngine ? "Online Reservation" : "Staff"),
            },
        },
        {
            id: "code",
            header: t("Reservation code"),
            accessorFn: row => row.code,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 170,
            meta: {defaultActive: false, isServerSortable: true, printable: true},
        },
        {
            id: "email",
            header: t("Email"),
            accessorFn: row => row.primaryEmail,
            cell: ({getValue}) => getValue<string | undefined>() ?? null,
            size: 260,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                serverSortField: "primaryEmail",
                printable: true,
            },
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: row => row.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        createReservationUpdatedColumn<WeeklyPlannerReservationListRowDTO>(t("Reservation updated")),
    ], [ageCategories, queryRange.start, locale, t]);

    const controller = useServerTable<WeeklyPlannerReservationListRowDTO>({
        tableId: "weekly-planner-reservation-list-v1",
        columns,
        getRowId: row => row.id,
        initialSorting: [{id: "start", desc: false}],
        onRowClick: handleOpenReservation,
        queryDependencies: [
            sectorId,
            queryRange.start.getTime(),
            queryRange.end.getTime(),
            serviceSelection.services.length,
            serviceSelection.allSelected,
            selectedServiceIdsKey,
        ],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            if (noServicesSelected) {
                return {content: [], totalElements: 0};
            }

            const page = await ReservationAggregationService.listWeeklyPlanner({
                sectorId,
                serviceIds: selectedServiceIds,
                search: searchQuery || undefined,
                filters: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                overlaps: {
                    start: startOfDay(queryRange.start),
                    end: endOfDay(queryRange.end),
                },
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    // The export's request: the list's own, without paging; nothing while no service is picked.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery,
    ): WeeklyPlannerReservationListRequestDTO | undefined => noServicesSelected ? undefined : {
        sectorId,
        serviceIds: selectedServiceIds,
        search: searchQuery || undefined,
        filters: filterRules.length
            ? {
                filterGroups: [{
                    filterRules,
                    operator: DataFilterOperator.AND,
                }],
            }
            : undefined,
        overlaps: {
            start: startOfDay(queryRange.start),
            end: endOfDay(queryRange.end),
        },
        limit: serverTableExportLimit(sorting),
    }, [noServicesSelected, queryRange.end, queryRange.start, sectorId, selectedServiceIds]);

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <RightSidebarRailHeader>
                            <RightSidebarRailHeaderCenter>
                                <RangeDatePicker
                                    value={dateRange}
                                    onChange={onRangeChange}
                                    showStepButtons
                                    size="medium"
                                />
                            </RightSidebarRailHeaderCenter>
                        </RightSidebarRailHeader>

                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ServerTableRailFilter
                                options={filterOptionsQuery.data ?? []}
                                disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                            />
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <RightSidebarCreateButton
                                label={t("Create reservation")}
                                disabled={readOnly}
                                onClick={handleCreateReservation}
                            />
                            <RightSidebarDivider/>
                            <ReservationServicePicker selection={serviceSelection}/>
                            <ServerTableRailPrint title={t("Reservations")}/>
                            <ServerTableRailExport
                                list="weekly-planner-reservations"
                                title={t("Reservations")}
                                buildRequest={exportRequest}
                            />
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(undefined)}
                onSave={() => {
                    setSelectedReservation(undefined);
                    void controller.query.refetch();
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}
